// M-76 (promo): HOT 案件（docs/INTEREST-ENGINE-DESIGN.md §7 / タイプ②長期検討型）の判定純関数。
// 「担当者通知 × 自動 Act 誘導の並走 + 排他制御」のうち、**判定と状態遷移の規則だけ**を持つ。
// 副作用（doc 書込 / 通知送信）は functions/src/promo/hotLead.ts・hotLeadDetect.ts が担う。
//
// SDK 非依存（fuelPriceDerive.ts と同じ方針）。import は promoHot → promoEngine →
// promoInterest の一方向（循環なし）。
//
// fail 方針は既存 3 兄弟（promoInterest / promoCycle / promoChurn）に揃える:
//   - 基準時刻（evaluatedAt）の parse 不能は throw RangeError（黙って fail-open にしない）
//   - 個別データの不正・欠落は無音 skip
//   - 判定材料不足は fail-closed（HOT 起票・自動フォローを**発火させない**側に倒す）
import { HOT_LEAD_NOTES_MAX, SURVEY_CHOICE_KEYS } from './types.js';
import { DEFAULT_STAGE_PROMOTION_RULES, isSurveyTouch, jstDayNumber, resolveInterestKeyFromEvent, } from './promoInterest.js';
/**
 * アンケート回答の「関心の強さ」序列（大きいほど強い）。
 *
 * `SURVEY_CHOICE_KEYS` の並びは**抑制優先**（`not_interested` が先頭 = 同時刻タイブレークで
 * 勝つ）ための順序であり、意味的な強さの序列とはたまたま一致しているだけなので、
 * HOT 判定は配列インデックスに依存せず専用の map を持つ（片方の並び替えで他方が壊れない）。
 */
const CHOICE_RANK = {
    not_interested: 0,
    slightly_interested: 1,
    interested_undecided: 2,
    interested_planned: 3,
};
/**
 * その商材について顧客が「関心なし」を自己申告しているか（§6 A×self_declared / 嫌われ防止）。
 *
 * 配信抑制（`matchesSegment`）と HOT 起票（`promoHotLeadDetectDaily`）が**同じ事実**で
 * 判断するための共有述語。片方だけに実装すると「配信は止めたのに、同じ客が行動したら
 * 担当者に HOT 通知が飛ぶ」という非対称が生まれる（レビュー指摘）。
 */
export function isInterestOptedOut(answers, key) {
    return answers?.[key]?.answer === 'not_interested';
}
/** 回答の関心の強さ序列（未知値は -1 = 「回答なし」より弱い扱い）。 */
export function surveyChoiceRank(choice) {
    if (!choice || !SURVEY_CHOICE_KEYS.includes(choice))
        return -1;
    return CHOICE_RANK[choice];
}
/**
 * §7「定期アンケート回答変化（**時期の具体化**）」の判定（即時 HOT のトリガー）。
 *
 * HOT = 今回の回答が `interested_planned`（関心あり・時期具体）で、**かつ前回より強い**。
 * - `interested_undecided`（関心あり・時期未定）は追跡モードの継続であって HOT ではない
 *   （§5 タイプ②は D が減衰しないため、時期未定のまま保持される）
 * - 同じ `interested_planned` の再回答は変化なし = 発火しない（通知の連打防止）
 * - 前回回答なし（初回）・前回が未知値は rank -1 として扱い発火する
 *
 * ステージ軸では `interested_undecided` と `interested_planned` はどちらも `desire` で
 * 同値のため（`stageGrantedBySurveyChoice`）、この区別は**回答値からしか得られない**。
 */
export function isSurveyHotEscalation(args) {
    if (args.choice !== 'interested_planned')
        return false;
    return surveyChoiceRank(args.previousChoice) < CHOICE_RANK.interested_planned;
}
/**
 * §7「行動の急増」の判定（日次バッチのトリガー）。
 *
 * 接触の定義は M-71 / M-72 と同一（`rules.toInterest` の能動接触 + 付与に至る survey 回答 =
 * `isSurveyTouch`）で、**同一 JST 暦日は 1 接触**に潰す。窓は評価日から遡る片側窓で、
 * 「N 日以内」の境界は既存規約（`applyStageDecay` の `evalDay - touchDay <= days`）に揃える。
 *
 * - `evaluatedAt` が parse 不能なら throw（基準時刻の黙殺 = fail-open を防ぐ）
 * - 評価日より後（未来）・時刻不明のイベントは無視する
 * - 商材が解決できないイベント（`resolveInterestKeyFromEvent` が null）は無視する
 * - `threshold < 1` は設定不正として発火させない（fail-closed）
 */
export function evaluateActivitySurge(events, key, evaluatedAt, hot, config, rules = DEFAULT_STAGE_PROMOTION_RULES) {
    const evalMs = Date.parse(evaluatedAt);
    if (!Number.isFinite(evalMs)) {
        throw new RangeError(`evaluateActivitySurge: invalid evaluatedAt: ${evaluatedAt}`);
    }
    const windowDays = hot.surgeWindowDays;
    const threshold = hot.surgeEventCount;
    const base = {
        windowDays,
        threshold,
    };
    const evalDay = jstDayNumber(evalMs);
    const days = new Set();
    for (const event of events) {
        if (!rules.toInterest.includes(event.eventType) && !isSurveyTouch(event))
            continue;
        const at = Date.parse(event.createdAt);
        if (!Number.isFinite(at))
            continue;
        const day = jstDayNumber(at);
        if (day > evalDay || evalDay - day > windowDays)
            continue;
        if (resolveInterestKeyFromEvent(event, config) !== key)
            continue;
        days.add(day);
    }
    const touchDays = days.size;
    return { ...base, touchDays, surged: threshold >= 1 && touchDays >= threshold };
}
/**
 * §7「担当者が 3 日（テナント設定）無対応なら自動フォロー発火」の判定。
 *
 * 起点は `PromoHotLead.lastActionAt`（担当者の最終対応 = ステータス変更 / メモ追記）。
 * 起票直後に対応開始した案件は、その時点から改めて猶予日数を数え直す。
 * 「N 日無対応」= JST 暦日差が N 以上（`evalDay - actionDay >= followUpAfterDays`）。
 *
 * 再発火ガード: 前回の自動フォロー（`followUpSentAt`）が最終対応以降なら発火しない。
 * これにより「1 回の無対応期間につき 1 回」に収束し、担当者が動いて `lastActionAt` が
 * 進んだ後に再び放置されたときだけ次のフォローが出る（日次バッチの毎日通知を防ぐ）。
 *
 * - `evaluatedAt` が parse 不能なら throw
 * - `lastActionAt` の欠落・parse 不能は false（判定材料なしで通知を撃たない = fail-closed）
 * - `followUpAfterDays < 1` は設定不正として発火させない（起票と同時の暴発を防ぐ）
 */
export function isFollowUpDue(args) {
    const evalMs = Date.parse(args.evaluatedAt);
    if (!Number.isFinite(evalMs)) {
        throw new RangeError(`isFollowUpDue: invalid evaluatedAt: ${args.evaluatedAt}`);
    }
    if (args.followUpAfterDays < 1)
        return false;
    const actionMs = args.lastActionAt !== undefined ? Date.parse(args.lastActionAt) : NaN;
    if (!Number.isFinite(actionMs))
        return false;
    const sentMs = args.followUpSentAt !== undefined ? Date.parse(args.followUpSentAt) : NaN;
    if (Number.isFinite(sentMs) && sentMs >= actionMs)
        return false;
    return jstDayNumber(evalMs) - jstDayNumber(actionMs) >= args.followUpAfterDays;
}
/**
 * HOT を検知したときに「起票するか / 何もしないか」を決める（§7 クローズと再燃の規則）。
 *
 * - 既存なし → `create`（新規起票 + 担当者通知）
 * - 既存が open（`new` / `in_progress`）→ `skip_open`（担当者が既に把握しているので**通知しない**。
 *   同じ案件で毎日通知が鳴るのを防ぐ）
 * - 既存がクローズ済み（`won` / `lost`）→ 冷却期間を過ぎていれば `reopen`、未経過なら
 *   `skip_cooldown`。冷却が無いと `lost` にした翌日のバッチで再 HOT 化して通知が鳴り続ける
 * - `closedAt` の欠落・parse 不能は `skip_cooldown`（材料なしで通知を撃たない = fail-closed。
 *   手動でステータスを `in_progress` に戻せば運用は再開できる）
 * - `reopenCooldownDays <= 0` は「冷却なし」を意味する明示値として即 `reopen` を許す
 */
export function planHotLeadDetection(args) {
    const evalMs = Date.parse(args.evaluatedAt);
    if (!Number.isFinite(evalMs)) {
        throw new RangeError(`planHotLeadDetection: invalid evaluatedAt: ${args.evaluatedAt}`);
    }
    const existing = args.existing;
    if (!existing)
        return 'create';
    if (existing.status === 'new' || existing.status === 'in_progress')
        return 'skip_open';
    if (args.reopenCooldownDays <= 0)
        return 'reopen';
    const closedMs = existing.closedAt !== undefined ? Date.parse(existing.closedAt) : NaN;
    if (!Number.isFinite(closedMs))
        return 'skip_cooldown';
    return jstDayNumber(evalMs) - jstDayNumber(closedMs) >= args.reopenCooldownDays
        ? 'reopen'
        : 'skip_cooldown';
}
/**
 * ステータス遷移の可否（§7「新規 → 対応中 → 成約 / 見送り」）。
 *
 * - 同一ステータスへの遷移は false（no-op / 呼出側が冪等 skip と区別できるようにする）
 * - `new` へ**戻す**遷移は不可 — `new` は起票と自動 reopen 専用の入口で、手動操作の
 *   選択肢には出さない（担当者の対応履歴を「未対応」に戻すと自動フォローの起点が曖昧になる）
 * - それ以外は許可する。`won` / `lost` → `in_progress` は再商談・掘り起こしの正当な運用
 */
export function canTransitionHotLeadStatus(from, to) {
    if (from === to)
        return false;
    return to !== 'new';
}
/**
 * 対応記録の追記（非破壊 / 上限超過は**古い順**に切り捨て）。
 * doc サイズ 1MiB 制約に対する保険で、履歴の真実源は `audit_logs`（ステータス変更）と
 * 直近 `max` 件のメモ。
 */
export function appendHotLeadNote(current, note, max = HOT_LEAD_NOTES_MAX) {
    const next = [...(current ?? []), note];
    return next.length > max ? next.slice(next.length - max) : next;
}
/**
 * §7 排他制御: この顧客×商材への自動配信を抑制すべきか（`matchesSegment` の判定本体）。
 *
 * 抑制するのは「担当者が対応中」かつ「自動フォローが排他を解除していない」場合のみ。
 * `new`（起票直後・担当者未着手）は**抑制しない** — 誰も商談していない以上、機械トラックの
 * Act 誘導を止める理由がなく、§7 の「人と機械の 2 トラック並走」に反するため。
 */
export function isHotDeliverySuppressed(mirror) {
    if (!mirror || mirror.status !== 'in_progress')
        return false;
    return mirror.suppressionReleased !== true;
}
/**
 * `promo_hot_leads` の決定論 doc id（顧客×商材で 1 doc）。
 * 排他の単位・冪等の単位・一覧の行をすべて一致させるために決定論にする
 * （`promo_survey_responses` / `promo_plans` と同方針）。
 */
export function buildHotLeadId(tenantId, userId, interestKey) {
    return `${tenantId}__${userId}__${interestKey}`;
}
//# sourceMappingURL=promoHot.js.map